package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Popover
import co.maytes.standupbot.dashboard.components.widgets.PopoverPlacement
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.standupruns.formatRelative
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.workspace.MemberDisabledReasonDto
import co.maytes.standupbot.shared.dto.workspace.MemberStatusDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import co.maytes.standupbot.shared.policy.MemberPermissions
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.datetime.Clock

val MemberRowStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("transition", Motion.TRANSITION_HOVER)
            property("background-color", "transparent")
        }
    }
    hover {
        Modifier.backgroundColor(Palette.surfaceHover)
    }
}

val IdentityLinkStyle = CssStyle {
    hover {
        Modifier.styleModifier { property("text-decoration", "underline") }
    }
}

sealed interface MemberAction {
    data object ChangeRoleToAdmin : MemberAction
    data object ChangeRoleToMember : MemberAction
    data object ChangeRoleToViewer : MemberAction
    data object ResendInvite : MemberAction
    data object CopyInviteLink : MemberAction
    data object RevokeInvite : MemberAction
    data object Disable : MemberAction
    data object Restore : MemberAction
    data object Erase : MemberAction
    data object TransferOwnership : MemberAction
    data object LinkLinear : MemberAction
    data object LinkGithub : MemberAction
}

fun availableMemberActions(
    actor: RoleDto?,
    member: WorkspaceMemberDto,
    hasFreshInviteLink: Boolean = false,
): List<MemberAction> {
    if (actor == null || member.isCurrentUser) return emptyList()
    return when (member.status) {
        MemberStatusDto.INVITED -> pendingInviteActions(actor, member, hasFreshInviteLink)
        MemberStatusDto.DISABLED -> disabledMemberActions(actor, member)
        MemberStatusDto.ACTIVE -> activeMemberActions(actor, member)
    }
}

private fun activeMemberActions(
    actor: RoleDto,
    member: WorkspaceMemberDto,
): List<MemberAction> =
    buildList {
        if (MemberPermissions.canChangeRole(actor, member.role, RoleDto.ADMIN) && member.role != RoleDto.ADMIN) {
            add(MemberAction.ChangeRoleToAdmin)
        }
        if (MemberPermissions.canChangeRole(actor, member.role, RoleDto.MEMBER) && member.role != RoleDto.MEMBER) {
            add(MemberAction.ChangeRoleToMember)
        }
        if (MemberPermissions.canChangeRole(actor, member.role, RoleDto.VIEWER) && member.role != RoleDto.VIEWER) {
            add(MemberAction.ChangeRoleToViewer)
        }
        if (MemberPermissions.canTransferOwnership(actor) && member.role != RoleDto.OWNER) add(MemberAction.TransferOwnership)
        if (MemberPermissions.canChangeStatus(actor, member.role)) add(MemberAction.Disable)
    }

private fun disabledMemberActions(
    actor: RoleDto,
    member: WorkspaceMemberDto,
): List<MemberAction> {
    if (member.disabledReason == MemberDisabledReasonDto.ERASED) return emptyList()
    return buildList {
        if (MemberPermissions.canChangeStatus(actor, member.role)) add(MemberAction.Restore)
        if (MemberPermissions.canEraseOthers(actor)) add(MemberAction.Erase)
    }
}

private fun pendingInviteActions(
    actor: RoleDto,
    member: WorkspaceMemberDto,
    hasFreshInviteLink: Boolean,
): List<MemberAction> {
    if (!MemberPermissions.canInvite(actor, member.role)) return emptyList()
    return buildList {
        add(MemberAction.ResendInvite)
        if (hasFreshInviteLink) add(MemberAction.CopyInviteLink)
        if (member.role != RoleDto.ADMIN && MemberPermissions.canGrant(actor, RoleDto.ADMIN)) add(MemberAction.ChangeRoleToAdmin)
        if (member.role != RoleDto.MEMBER && MemberPermissions.canGrant(actor, RoleDto.MEMBER)) add(MemberAction.ChangeRoleToMember)
        if (member.role != RoleDto.VIEWER && MemberPermissions.canGrant(actor, RoleDto.VIEWER)) add(MemberAction.ChangeRoleToViewer)
        add(MemberAction.RevokeInvite)
    }
}

private val InlineInviteActions = setOf(MemberAction.ResendInvite, MemberAction.CopyInviteLink)

private val DestructiveActions = setOf(MemberAction.Disable, MemberAction.Erase, MemberAction.RevokeInvite)

@Composable
fun MemberRow(
    member: WorkspaceMemberDto,
    canManage: Boolean,
    showEmail: Boolean,
    lastOwner: Boolean,
    actions: List<MemberAction>,
    onAction: (MemberAction) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    val menuActions = if (member.status == MemberStatusDto.INVITED) actions.filterNot { it in InlineInviteActions } else actions
    val linkable = canManage && member.status == MemberStatusDto.ACTIVE

    Row(
        modifier = MemberRowStyle.toModifier()
            .classNames(MembersClasses.ROW)
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs)
            .styleModifier {
                property("min-height", MEMBERS_ROW_MIN_HEIGHT)
                property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(modifier = Modifier.classNames(MembersClasses.PERSON).styleModifier { property("min-width", "0") }) {
            PersonCell(member = member, showEmail = showEmail)
        }
        Box(modifier = Modifier.classNames(MembersClasses.ROLE)) { RoleCell(member) }
        Box(modifier = Modifier.classNames(MembersClasses.ACCOUNTS).styleModifier { property("min-width", "0") }) {
            if (member.status != MemberStatusDto.INVITED) {
                AccountsCell(member = member, linkable = linkable, onAction = onAction)
            }
        }
        Box(modifier = Modifier.classNames(MembersClasses.STATUS)) { StatusCell(member) }
        Box(modifier = Modifier.classNames(MembersClasses.JOINED)) { JoinedCell(member) }

        Row(
            modifier = Modifier.classNames(MembersClasses.ACTIONS).gap(Spacing.xs).styleModifier {
                property("position", "relative")
                property("justify-self", "end")
            },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (member.status == MemberStatusDto.INVITED) {
                actions.filter { it in InlineInviteActions }.take(1).forEach { action ->
                    Button(
                        label = if (action == MemberAction.ResendInvite) "Resend" else "Copy link",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { onAction(action) },
                    )
                }
            }
            if (menuActions.isNotEmpty()) {
                Button(
                    label = "Manage",
                    variant = ButtonVariant.Ghost,
                    size = ButtonSize.Sm,
                    modifier = Modifier.attrsModifier {
                        attr("aria-label", "Manage ${member.displayName}")
                        attr("aria-haspopup", "menu")
                        attr("aria-expanded", menuOpen.toString())
                    },
                    onClick = { menuOpen = !menuOpen },
                )
            }
            MemberActionPopover(
                open = menuOpen && menuActions.isNotEmpty(),
                actions = menuActions,
                lastOwner = lastOwner,
                onPick = { action ->
                    menuOpen = false
                    onAction(action)
                },
                onDismiss = { menuOpen = false },
            )
        }
    }
}

@Composable
private fun PersonCell(
    member: WorkspaceMemberDto,
    showEmail: Boolean,
) {
    Row(
        modifier = Modifier
            .gap(Spacing.sm)
            .styleModifier { property("min-width", "0") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Avatar(
            name = member.displayName,
            src = member.avatarUrl,
            sizePx = AVATAR_PX,
            tone = AvatarTone.Muted,
        )
        Column(
            modifier = Modifier.styleModifier {
                property("min-width", "0")
                property("gap", "${Space.s0_5}")
            },
        ) {
            SpanText(
                text = member.displayName + if (member.isCurrentUser) " (you)" else "",
                modifier = Modifier
                    .fontSize(Typography.label.size)
                    .fontWeight(Typography.label.weight)
                    .color(if (member.status == MemberStatusDto.DISABLED) Palette.textTertiary else Palette.textPrimary)
                    .ellipsis(),
            )
            if (showEmail) {
                SpanText(
                    text = member.email ?: noEmailText(member),
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(if (member.email == null) Palette.textTertiary else Palette.textSecondary)
                        .ellipsis(),
                )
            }
        }
    }
}

private const val AVATAR_PX = 32

private fun Modifier.ellipsis(): Modifier =
    styleModifier {
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }

@Composable
private fun RoleCell(member: WorkspaceMemberDto) {
    SpanText(
        text = MembersCopy.roleLabel(member.role),
        modifier = Modifier
            .fontSize(Typography.label.size)
            .fontWeight(400)
            .color(if (member.status == MemberStatusDto.DISABLED) Palette.textTertiary else Palette.textSecondary),
    )
}

@Composable
private fun AccountsCell(
    member: WorkspaceMemberDto,
    linkable: Boolean,
    onAction: (MemberAction) -> Unit,
) {
    val erased = member.disabledReason == MemberDisabledReasonDto.ERASED
    Row(
        modifier = Modifier.gap(Spacing.xs).styleModifier { property("flex-wrap", "wrap") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        AccountChip(
            brand = Brand.Slack,
            value = member.slackUserId.takeUnless { erased },
            interactive = false,
            actionLabel = "",
            onClick = {},
        )
        AccountChip(
            brand = Brand.Linear,
            value = member.linearUserId,
            interactive = linkable,
            actionLabel = "Link Linear account for ${member.displayName}",
            onClick = { onAction(MemberAction.LinkLinear) },
        )
        AccountChip(
            brand = Brand.GitHub,
            value = member.githubLogin?.let { "@$it" },
            interactive = linkable,
            actionLabel = "Link GitHub account for ${member.displayName}",
            onClick = { onAction(MemberAction.LinkGithub) },
        )
    }
}

@Composable
private fun AccountChip(
    brand: Brand,
    value: String?,
    interactive: Boolean,
    actionLabel: String,
    onClick: () -> Unit,
) {
    val linked = !value.isNullOrBlank()
    val tooltip = if (linked) "${brand.label}: $value" else if (interactive) actionLabel else "${brand.label}: not linked"
    val base =
        if (interactive && !linked) {
            IdentityLinkStyle.toModifier()
                .pressable()
                .cursor(Cursor.Pointer)
                .role("button")
                .tabIndex(0)
                .attrsModifier { attr("aria-label", actionLabel) }
                .onClick { onClick() }
                .onKeyDown { event ->
                    if (event.key == "Enter" || event.key == " ") {
                        event.preventDefault()
                        onClick()
                    }
                }
        } else {
            Modifier
        }
    Box(modifier = base.attrsModifier { attr("title", tooltip) }) {
        BrandLogo(brand = brand, sizePx = ACCOUNT_CHIP_PX, glyphPx = ACCOUNT_GLYPH_PX, dimmed = !linked)
    }
}

private const val ACCOUNT_CHIP_PX = 28
private const val ACCOUNT_GLYPH_PX = 16

@Composable
private fun StatusCell(member: WorkspaceMemberDto) {
    when (member.status) {
        MemberStatusDto.INVITED -> InvitedStatus(member)
        MemberStatusDto.DISABLED -> DisabledStatus(member)
        MemberStatusDto.ACTIVE ->
            SpanText(
                text = "Active",
                modifier = Modifier
                    .fontSize(Typography.label.size)
                    .fontWeight(400)
                    .color(Palette.textSecondary),
            )
    }
}

@Composable
private fun DisabledStatus(member: WorkspaceMemberDto) {
    Column(modifier = Modifier.styleModifier { property("gap", "${Space.s0_5}") }) {
        Badge(label = disabledStatusLabel(member), tone = BadgeTone.Neutral)
        member.disabledAt?.let { disabledAt ->
            SpanText(
                text = formatRelative(disabledAt),
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .fontWeight(400)
                    .color(Palette.textTertiary),
            )
        }
    }
}

@Composable
private fun InvitedStatus(member: WorkspaceMemberDto) {
    val expired = member.inviteExpiresAt?.let { it <= Clock.System.now() } == true
    Column(modifier = Modifier.styleModifier { property("gap", "${Space.s0_5}") }) {
        Badge(
            label = if (expired) "Expired" else "Invited",
            tone = if (expired) BadgeTone.Neutral else BadgeTone.Warning,
        )
        member.inviteLastSentAt?.let { sentAt ->
            SpanText(
                text = "sent ${formatRelative(sentAt)}",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .fontWeight(400)
                    .color(Palette.textTertiary),
            )
        }
    }
}

@Composable
private fun JoinedCell(member: WorkspaceMemberDto) {
    SpanText(
        text = MembersCopy.joined(member),
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .fontWeight(400)
            .color(if (member.joinedAt == null) Palette.textTertiary else Palette.textSecondary)
            .styleModifier {
                property("text-align", "right")
                property("font-variant-numeric", "tabular-nums")
            },
    )
}

@Composable
private fun MemberActionPopover(
    open: Boolean,
    actions: List<MemberAction>,
    lastOwner: Boolean,
    onPick: (MemberAction) -> Unit,
    onDismiss: () -> Unit,
) {
    Popover(
        open = open,
        placement = PopoverPlacement.BottomEnd,
        onDismiss = onDismiss,
        offset = Spacing.xxs,
        role = "menu",
        ariaLabel = "Member actions",
        modifier = Modifier
            .styleModifier {
                property("min-width", "200px")
                property("box-shadow", Shadow.LG)
                property("border", "1px solid ${Palette.border.cssRgb()}")
            }
            .backgroundColor(Palette.surface)
            .borderRadius(Radius.md)
            .padding(Spacing.xxs)
            .onClick { evt -> evt.stopPropagation() },
    ) {
        actions.forEach { action ->
            MenuItem(
                label = action.menuLabel(),
                destructive = action in DestructiveActions,
                enabled = !lastOwner || action == MemberAction.ResendInvite || action == MemberAction.RevokeInvite,
                disabledHint = MembersCopy.LAST_OWNER_HINT,
                onClick = { onPick(action) },
            )
        }
    }
}

private fun MemberAction.menuLabel(): String = when (this) {
    MemberAction.ChangeRoleToAdmin -> "Change to Admin"
    MemberAction.ChangeRoleToMember -> "Change to Member"
    MemberAction.ChangeRoleToViewer -> "Change to Viewer"
    MemberAction.ResendInvite -> "Resend invite"
    MemberAction.CopyInviteLink -> "Copy invite link"
    MemberAction.RevokeInvite -> "Revoke invite"
    MemberAction.Disable -> "Disable"
    MemberAction.Restore -> "Restore"
    MemberAction.Erase -> "Erase personal data…"
    MemberAction.TransferOwnership -> "Transfer ownership…"
    MemberAction.LinkLinear -> "Link Linear"
    MemberAction.LinkGithub -> "Link GitHub"
}

@Composable
private fun MenuItem(
    label: String,
    onClick: () -> Unit,
    destructive: Boolean = false,
    enabled: Boolean = true,
    disabledHint: String? = null,
) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("width", "100%")
                property("transition", Motion.TRANSITION_HOVER)
                if (!enabled) property("opacity", "0.4")
            }
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.inner)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .color(if (destructive) Palette.status(StatusHue.Danger).solid else Palette.textPrimary)
            .role("menuitem")
            .tabIndex(if (enabled) 0 else -1)
            .attrsModifier {
                if (!enabled) {
                    attr("aria-disabled", "true")
                    disabledHint?.let { attr("title", it) }
                }
            }
            .onClick { if (enabled) onClick() }
            .onKeyDown { event ->
                if (enabled && (event.key == "Enter" || event.key == " ")) {
                    event.preventDefault()
                    onClick()
                }
            },
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500),
        )
    }
}

internal fun disabledStatusLabel(member: WorkspaceMemberDto): String =
    when (member.disabledReason) {
        MemberDisabledReasonDto.SLACK_DEACTIVATED -> "Disabled · Slack"
        MemberDisabledReasonDto.ERASED -> "Erased"
        MemberDisabledReasonDto.ADMIN, null -> "Disabled"
    }

internal fun slackUserText(member: WorkspaceMemberDto): String =
    when {
        member.disabledReason == MemberDisabledReasonDto.ERASED -> MembersCopy.NONE
        else -> member.slackUserId ?: MembersCopy.NONE
    }

internal fun noEmailText(member: WorkspaceMemberDto): String =
    when {
        member.status == MemberStatusDto.INVITED -> "Invited by Slack DM"
        member.disabledReason == MemberDisabledReasonDto.ERASED -> "Personal data erased"
        else -> "No email on file"
    }
